import { analyzeChord, analyzePitchClasses, canonicalNoteName, normalizePitchClass, pitchClassFromName, type ChordAnalysisResult, type RegisteredNoteInput } from '../core/chord';
import { ChordInputError } from '../core/chord/types';
import type { HarmonyAnalysis, HarmonyCandidate, HarmonyInput, HarmonyOptions, HarmonyProfile, HarmonyProgressionEvent, HarmonyProgressionResult, KeyCandidate, ProgressionEvent, ProgressionInput, TonalContext, TonalMode } from './types';
import { contextForPitchClass, createTonalContext, scalePitchClasses, TONAL_MODES } from './scale';
import { parseChordSymbol } from './symbol';
import { renderRoman, romanEvidence, romanForCandidate } from './roman';

export const DEFAULT_HARMONY_WEIGHTS = {
  diatonic: 1,
  borrowed: 0.76,
  applied: 0.9,
  cadence: 0.22,
  modulationPenalty: 0.16,
  chromaticPenalty: 0.42,
  voiceLeading: 0.08,
};

const MODE_PRIOR: Record<TonalMode, number> = { major: 0.08, naturalMinor: 0.07, harmonicMinor: 0.05, melodicMinor: 0.04, dorian: 0, phrygian: 0, lydian: 0, mixolydian: 0, locrian: 0 };

const PROFILE_WEIGHTS: Record<HarmonyProfile, Partial<typeof DEFAULT_HARMONY_WEIGHTS>> = {
  general: {},
  pop: { diatonic: 1.08, borrowed: 0.88, applied: 0.74, cadence: 0.14 },
  jazz: { diatonic: 0.92, borrowed: 0.82, applied: 1.08, cadence: 0.28, chromaticPenalty: 0.56 },
  classical: { diatonic: 1.06, borrowed: 0.72, applied: 0.94, cadence: 0.34, modulationPenalty: 0.22 },
};

function resolvedWeights(options: HarmonyOptions = {}) { return { ...DEFAULT_HARMONY_WEIGHTS, ...PROFILE_WEIGHTS[options.profile ?? 'general'], ...options.weights }; }

function isChordAnalysisResult(input: unknown): input is ChordAnalysisResult {
  return typeof input === 'object'
    && input !== null
    && 'candidates' in input
    && Array.isArray(input.candidates)
    && 'primary' in input
    && 'alternatives' in input
    && Array.isArray(input.alternatives)
    && 'relations' in input
    && Array.isArray(input.relations)
    && 'inputMode' in input
    && (input.inputMode === 'registered' || input.inputMode === 'pitch-class')
    && 'ambiguity' in input;
}

function isRegisteredNotes(input: unknown): input is readonly RegisteredNoteInput[] {
  return Array.isArray(input);
}

function validateHarmonyInput(input: unknown, label: string): asserts input is HarmonyInput {
  if (typeof input === 'string' || isRegisteredNotes(input) || isChordAnalysisResult(input)) return;
  throw new ChordInputError(`${label} must be a chord symbol, registered-note array, or chord analysis result`);
}

export function resolveHarmonyInput(input: HarmonyInput, options: HarmonyOptions = {}): ChordAnalysisResult {
  validateHarmonyInput(input, 'Harmony input');
  if (typeof input === 'string') return parseChordSymbol(input, options.grammar ?? 'standard').analysis;
  if (isChordAnalysisResult(input)) return input;
  return analyzeChord(input, { explain: true });
}

function normalizedContext(input: NonNullable<HarmonyOptions['key']>, source: TonalContext['source']): TonalContext {
  const tonicPitchClass = input.tonicPitchClass ?? pitchClassFromName(input.tonic);
  return createTonalContext({ tonic: input.tonic, tonicPitchClass, mode: input.mode, source });
}

function tonalFit(candidate: HarmonyCandidate, weights: ReturnType<typeof resolvedWeights>): number {
  if (candidate.function === 'diatonic') return weights.diatonic;
  if (candidate.function === 'borrowed') return weights.borrowed;
  if (candidate.function === 'tritoneSubstitution') return weights.applied + 0.22;
  if (candidate.function === 'appliedDominant' || candidate.function === 'appliedLeadingTone') return weights.applied + 0.08;
  if (candidate.function === 'neapolitan' || candidate.function === 'augmentedSixth' || candidate.function === 'commonToneDiminished') return weights.borrowed;
  if (candidate.function === 'chromaticMediant') return weights.chromaticPenalty + 0.12;
  return weights.chromaticPenalty;
}

export function analyzeHarmony(input: HarmonyInput, options: HarmonyOptions = {}): HarmonyAnalysis {
  const analysis = resolveHarmonyInput(input, options);
  const keyCandidates = options.key
    ? [{ context: normalizedContext(options.key, 'manual'), score: 1, confidence: 1, evidence: ['Manual tonal context'] }]
    : options.auto === false ? [] : inferKeys([analysis], options);
  const context = keyCandidates[0]?.context ?? null;
  if (!context) return { input: analysis, context: null, keyCandidates, primary: null, alternatives: [], candidates: [], unknown: true, evidence: ['No tonal context could be inferred'] };
  const weights = resolvedWeights(options);
  const candidates = analysis.candidates.map((chord) => {
    const roman = romanForCandidate(chord, context, options.profile);
    const functionFit = tonalFit({ chord, roman, renderings: renderRoman(roman), tonalScore: 0, combinedScore: 0, function: roman.function, evidence: [] }, weights);
    const tonalScore = Number(Math.min(1.25, functionFit).toFixed(3));
    const combinedScore = Number((chord.score * tonalScore).toFixed(3));
    const renderings = renderRoman(roman);
    return { chord, roman, renderings, tonalScore, combinedScore, function: roman.function, evidence: romanEvidence(chord, roman, context) } satisfies HarmonyCandidate;
  }).sort((left, right) => right.combinedScore - left.combinedScore || right.chord.score - left.chord.score || left.renderings.analysis.localeCompare(right.renderings.analysis));
  const primary = candidates[0] ?? null;
  return { input: analysis, context, keyCandidates, primary, alternatives: primary ? candidates.slice(1) : [], candidates, unknown: !primary || primary.tonalScore < 0.5, evidence: primary?.evidence ?? ['No harmony candidate'] };
}

function isProgressionEvent(input: unknown): input is ProgressionEvent {
  return typeof input === 'object' && input !== null && !Array.isArray(input) && 'input' in input;
}

function validateEventTick(value: number | undefined, label: string): void {
  if (value !== undefined && (!Number.isSafeInteger(value) || value < 0)) {
    throw new ChordInputError(`${label} must be a non-negative safe integer: ${value}`);
  }
}

function normalizeProgressionEvents(input: unknown): ProgressionEvent[] {
  if (!Array.isArray(input)) throw new ChordInputError('Progression input must be an array');
  return input.map((item, index) => {
    let event: ProgressionEvent;
    if (isProgressionEvent(item)) {
      validateHarmonyInput(item.input, `Progression event ${index} input`);
      event = { ...item };
    } else {
      validateHarmonyInput(item, `Progression event ${index} input`);
      event = { input: item, id: `event-${index}` };
    }
    if (event.chord !== undefined && !isChordAnalysisResult(event.chord)) {
      throw new ChordInputError(`Progression event ${index} chord must be a chord analysis result`);
    }
    validateEventTick(event.start, `Progression event ${index} start`);
    validateEventTick(event.end, `Progression event ${index} end`);
    if (event.start !== undefined && event.end !== undefined && event.end < event.start) {
      throw new ChordInputError(`Progression event ${index} end must be >= start`);
    }
    return { ...event, id: event.id ?? `event-${index}` };
  });
}

function progressionAnalyses(input: ProgressionInput, options: HarmonyOptions): Array<{ event: ProgressionEvent; analysis: ChordAnalysisResult }> {
  return normalizeProgressionEvents(input).map((event) => ({
    event,
    analysis: event.chord ?? resolveHarmonyInput(event.input, options),
  }));
}

function cadenceBonus(analyses: readonly ChordAnalysisResult[], context: TonalContext, weights: ReturnType<typeof resolvedWeights>): number {
  let bonus = 0;
  // Keep event positions intact; filtering unknown roots would create false adjacency.
  const roots = analyses.map((analysis) => analysis.primary?.rootPitchClass ?? null);
  for (let index = 0; index < roots.length - 1; index += 1) {
    if (roots[index] === normalizePitchClass(context.tonicPitchClass + 7) && roots[index + 1] === context.tonicPitchClass) bonus += weights.cadence;
    if (roots[index] === normalizePitchClass(context.tonicPitchClass + 2) && roots[index + 1] === normalizePitchClass(context.tonicPitchClass + 7)) bonus += weights.cadence * 0.4;
  }
  return bonus;
}

function chordFit(analysis: ChordAnalysisResult, context: TonalContext, weights: ReturnType<typeof resolvedWeights>, profile: HarmonyProfile = 'general'): number {
  const perChord = analysis.candidates.map((chord) => {
    const roman = romanForCandidate(chord, context, profile);
    return chord.score * tonalFit({ chord, roman, renderings: renderRoman(roman), tonalScore: 0, combinedScore: 0, function: roman.function, evidence: [] }, weights);
  });
  const root = analysis.primary?.rootPitchClass;
  const scale = scalePitchClasses(context);
  return (Math.max(0, ...perChord) || 0) + (root === undefined ? 0 : scale.includes(root) ? 0.22 : -0.12);
}

function contextAnchor(analyses: readonly ChordAnalysisResult[], context: TonalContext): number {
  if (!analyses.length) return 0;
  const first = analyses[0]?.primary?.rootPitchClass;
  const last = analyses.at(-1)?.primary?.rootPitchClass;
  let score = 0;
  if (first === context.tonicPitchClass) score += 0.52;
  if (last === context.tonicPitchClass) score += 0.36;
  // Natural minor is the least assumptive minor reading for a tonic-anchored phrase.
  if (context.mode === 'naturalMinor' && (first === context.tonicPitchClass || last === context.tonicPitchClass)) score += 0.08;
  const tonicEvents = analyses.filter((analysis) => analysis.primary?.rootPitchClass === context.tonicPitchClass);
  if (tonicEvents.length) {
    const hasMinorTonic = tonicEvents.some((analysis) => {
      const quality = analysis.primary?.quality ?? '';
      return quality.startsWith('m') && !quality.startsWith('maj');
    });
    const hasMajorTonic = tonicEvents.some((analysis) => {
      const quality = analysis.primary?.quality ?? '';
      return Boolean(analysis.primary) && (!quality.startsWith('m') || quality.startsWith('maj')) && !quality.startsWith('dim');
    });
    if (context.mode === 'major' && hasMinorTonic && !hasMajorTonic) score -= 0.24;
    if (context.mode !== 'major' && hasMajorTonic && !hasMinorTonic) score -= 0.24;
  }
  return score;
}

export function inferKeys(input: readonly (HarmonyInput | ProgressionEvent)[], options: HarmonyOptions = {}): KeyCandidate[] {
  const analyses = progressionAnalyses(input as ProgressionInput, options).map((entry) => entry.analysis);
  if (options.auto === false && !options.key) return [];
  if (analyses.length === 0 || !analyses.some((analysis) => analysis.candidates.length > 0)) return [];
  const weights = resolvedWeights(options);
  const modes = options.modes?.length ? options.modes : TONAL_MODES;
  const candidates: KeyCandidate[] = [];
  for (let tonic = 0; tonic < 12; tonic += 1) for (const mode of modes) {
    const context = contextForPitchClass(tonic, mode, 'automatic');
    const fit = analyses.reduce((total, analysis) => total + chordFit(analysis, context, weights, options.profile), 0);
    const cadence = cadenceBonus(analyses, context, weights);
    const anchor = contextAnchor(analyses, context);
    const score = Number((fit / Math.max(1, analyses.length) + cadence + anchor + MODE_PRIOR[mode]).toFixed(4));
    const evidence = [`${analyses.length} analyzed harmony event(s)`, `diatonic/applied/borrowed deterministic profile: ${options.profile ?? 'general'}`, cadence ? `cadence bonus ${cadence.toFixed(3)}` : 'no cadence bonus', anchor ? `tonic anchor bonus ${anchor.toFixed(3)}` : 'no tonic anchor'];
    candidates.push({ context, score, confidence: 0, evidence });
  }
  candidates.sort((left, right) => right.score - left.score || left.context.label.localeCompare(right.context.label));
  const best = candidates[0]?.score ?? 1;
  const runnerUp = candidates[1]?.score ?? best;
  const margin = Math.max(0, best - runnerUp);
  const confidence = Number(Math.min(1, 0.5 + (margin / Math.max(0.5, Math.abs(best))) * 2).toFixed(3));
  return candidates.slice(0, options.maxKeyCandidates ?? 8).map((candidate, index) => ({
    ...candidate,
    confidence: index === 0 ? confidence : Number(Math.max(0, confidence - ((candidate.score === best ? 0 : (best - candidate.score) / Math.max(0.5, Math.abs(best))) * 0.5)).toFixed(3)),
  }));
}

function unresolvedFunctionalCandidate(event: HarmonyProgressionEvent, next: HarmonyProgressionEvent | undefined, options: HarmonyOptions): HarmonyProgressionEvent {
  const primary = event.analysis.primary;
  const nextRoot = next?.analysis.input.primary?.rootPitchClass;
  if (!primary || nextRoot === undefined) return event;
  const target = primary.function === 'tritoneSubstitution'
    ? normalizePitchClass(primary.chord.rootPitchClass - 1)
    : primary.function === 'appliedDominant'
      ? normalizePitchClass(primary.chord.rootPitchClass + 5)
      : primary.function === 'appliedLeadingTone'
        ? normalizePitchClass(primary.chord.rootPitchClass + 1)
        : null;
  if (target === null || target === nextRoot) return event;
  const roman = { ...primary.roman, function: 'chromatic' as const, appliedTarget: undefined, borrowedFrom: undefined };
  const renderings = renderRoman(roman);
  const tonalScore = Number(resolvedWeights(options).chromaticPenalty.toFixed(3));
  const downgraded = { ...primary, roman, renderings, tonalScore, combinedScore: Number((primary.chord.score * tonalScore).toFixed(3)), function: 'chromatic' as const, evidence: [...primary.evidence, `no following resolution to pitch class ${target}`] };
  const candidates = [downgraded, ...event.analysis.candidates.filter((candidate) => candidate !== primary)];
  return { ...event, analysis: { ...event.analysis, primary: downgraded, candidates, alternatives: candidates.slice(1), evidence: downgraded.evidence } };
}

function contextKey(context: TonalContext): string { return `${context.tonicPitchClass}:${context.mode}`; }

function segmentedContexts(analyses: readonly ChordAnalysisResult[], global: TonalContext, options: HarmonyOptions, globalKeys: readonly KeyCandidate[]): TonalContext[] {
  if (analyses.length < 4) return analyses.map(() => global);
  const weights = resolvedWeights(options);
  const modes = options.modes?.length ? options.modes : TONAL_MODES;
  const all = modes.flatMap((mode) => Array.from({ length: 12 }, (_, tonic) => contextForPitchClass(tonic, mode, 'automatic')));
  const pool = new Map<string, TonalContext>();
  [global, ...globalKeys.map((entry) => entry.context)].forEach((context) => pool.set(contextKey(context), context));
  analyses.forEach((analysis) => all
    .map((context) => ({ context, score: chordFit(analysis, context, weights, options.profile) }))
    .sort((left, right) => right.score - left.score)
    .slice(0, 4)
    .forEach((entry) => pool.set(contextKey(entry.context), entry.context)));
  const contexts = [...pool.values()];
  const fit = contexts.map((context) => analyses.map((analysis) => chordFit(analysis, context, weights, options.profile)));
  const prefix = fit.map((values) => {
    const sums = [0];
    values.forEach((value, index) => { sums[index + 1] = sums[index]! + value; });
    return sums;
  });
  const minLength = 2;
  const dp: Array<{ score: number; contextIndex: number; start: number; previous: number }> = [];
  for (let end = 0; end < analyses.length; end += 1) {
    let best = { score: -Infinity, contextIndex: 0, start: 0, previous: -1 };
    for (let contextIndex = 0; contextIndex < contexts.length; contextIndex += 1) {
      for (let start = 0; start <= end; start += 1) {
        const length = end - start + 1;
        // A leading segment may still be growing; every later segment must have
        // at least two events so a single applied chord cannot become a key.
        if (start > 0 && length < minLength) continue;
        const segmentScore = prefix[contextIndex]![end + 1]! - prefix[contextIndex]![start]! + (start === 0 ? contextAnchor(analyses, contexts[contextIndex]!) * 0.35 : 0) + (end === analyses.length - 1 && analyses[end]?.primary?.rootPitchClass === contexts[contextIndex]!.tonicPitchClass ? 0.36 : 0);
        if (start === 0) {
          if (segmentScore > best.score) best = { score: segmentScore, contextIndex, start, previous: -1 };
          continue;
        }
        const previousEnd = start - 1;
        const previous = dp[previousEnd];
        if (!previous || previous.contextIndex === contextIndex) continue;
        const score = previous.score + segmentScore - weights.modulationPenalty;
        if (score > best.score) best = { score, contextIndex, start, previous: previousEnd };
      }
    }
    dp[end] = best;
  }
  const segments: Array<{ start: number; end: number; contextIndex: number }> = [];
  let end = analyses.length - 1;
  while (end >= 0) {
    const state = dp[end]!;
    segments.unshift({ start: state.start, end, contextIndex: state.contextIndex });
    end = state.previous;
  }
  const result = analyses.map(() => global);
  segments.forEach((segment) => { for (let index = segment.start; index <= segment.end; index += 1) result[index] = contexts[segment.contextIndex]!; });
  return result;
}

export function analyzeProgression(input: ProgressionInput, options: HarmonyOptions = {}): HarmonyProgressionResult {
  const entries = progressionAnalyses(input, options);
  const analyses = entries.map((entry) => entry.analysis);
  const keys = options.key
    ? [{ context: normalizedContext(options.key, 'manual'), score: 1, confidence: 1, evidence: ['Manual tonal context'] }]
    : options.auto === false ? [] : inferKeys(analyses, options);
  const initialContext = keys[0]?.context ?? null;
  const allowAutomaticSegmentation = !options.key && options.auto !== false && (keys[0]?.confidence ?? 0) >= 0.72;
  const contexts = initialContext === null
    ? analyses.map(() => null)
    : options.key ? analyses.map(() => initialContext) : allowAutomaticSegmentation ? segmentedContexts(analyses, initialContext, options, keys) : analyses.map(() => initialContext);
  const globalContext = contexts[0] ?? initialContext;
  let events: HarmonyProgressionEvent[] = entries.map(({ event, analysis }, index) => {
    const override = options.overrides?.keyRanges?.find((range) => index >= range.startIndex && index <= range.endIndex);
    const localContext = override ? normalizedContext(override.context, 'override') : contexts[index] ?? null;
    const harmony = localContext
      ? analyzeHarmony(analysis, { ...options, key: { tonic: localContext.tonic, tonicPitchClass: localContext.tonicPitchClass, mode: localContext.mode } })
      : analyzeHarmony(analysis, { ...options, auto: false });
    return { index, id: event.id!, label: event.label, start: event.start ?? null, end: event.end ?? null, analysis: harmony, localContext, modulation: false };
  });
  events = events.map((event, index) => unresolvedFunctionalCandidate(event, events[index + 1], options));
  events.forEach((event, index) => {
    const previous = events[index - 1];
    event.modulation = Boolean(index > 0 && event.localContext && previous?.localContext
      && (event.localContext.tonicPitchClass !== previous.localContext.tonicPitchClass || event.localContext.mode !== previous.localContext.mode));
  });
  const tonalSegments = events.reduce<import('./types').TonalSegment[]>((segments, event) => {
    const localContext = event.localContext;
    if (!localContext) return segments;
    const previous = segments.at(-1);
    if (previous && previous.context.tonicPitchClass === localContext.tonicPitchClass && previous.context.mode === localContext.mode) previous.endIndex = event.index;
    else segments.push({ startIndex: event.index, endIndex: event.index, context: localContext, source: localContext.source, confidence: localContext.source === 'automatic' ? keys.find((candidate) => candidate.context.label === localContext.label)?.confidence ?? 0.5 : 1, reason: event.modulation ? 'local tonal evidence across adjacent events' : localContext.source === 'override' ? 'manual key-range override' : 'global tonal context' });
    return segments;
  }, []);
  return { globalContext, keyCandidates: keys, events, tonalSegments };
}

export function analyzeHarmonyNotes(notes: readonly RegisteredNoteInput[], options: HarmonyOptions = {}) { return analyzeHarmony(notes, options); }
export function analyzeHarmonyPitchClasses(input: readonly (number | string)[], options: HarmonyOptions = {}) { return analyzeHarmony(analyzePitchClasses(input, { explain: true }), options); }
export function keyName(tonicPitchClass: number, mode: TonalMode) { return `${canonicalNoteName(tonicPitchClass)} ${mode}`; }
